import { action, makeAutoObservable, reaction } from "mobx";
import INTOUCH_API from "../services/Api";
import {
  CURRENT_GAME_KEY,
  CURRENT_TEMP_DEMO_GENDER,
  CURRENT_USER_KEY,
} from "../utils/constants";
import Game from "./Game";
import Points from "./Points";
import Cookies from "js-cookie";

class PersonalAccount {
  games = [];
  user = {};
  statusGame = {};
  timerLinksMap = [];

  constructor() {
    makeAutoObservable(this);
    reaction(
      () => this.user,
      (user) => {
        const isEmptyObject =
          user && typeof user === "object" && !Object.keys(user).length;

        if (user && !isEmptyObject) {
          localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
        } else {
          localStorage.removeItem(CURRENT_USER_KEY);
        }
      }
    );
    const token = Cookies.get("token");
    if (!token) {
      const gender = JSON.parse(localStorage.getItem(CURRENT_TEMP_DEMO_GENDER));
      if (gender) {
        this.user.gender = gender;
      }
    }
  }

  async changeRules(isRulesAccepted) {
    await INTOUCH_API.requestManager(
      "change_rules",
      { isRulesAccepted, id: this.user.id },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = { ...this.user, isRulesAccepted };
        }
      })
    );
  }

  async addGameIdsByGreetings(gameId) {
    await INTOUCH_API.requestManager(
      "add_game_ids_by_greetings",
      { gameId, id: this.user.id },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = {
            ...this.user,
            gameIdsByGreetings: res.data.gameIdsByGreetings,
          };
        }
      })
    );
  }

  async addGameIdsByRules(gameId) {
    await INTOUCH_API.requestManager(
      "add_game_ids_by_rules",
      { gameId, id: this.user.id },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = { ...this.user, gameIdsByRules: res.data.gameIdsByRules };
        }
      })
    );
  }

  hasViewedRules(gameId) {
    return !!this.user?.gameIdsByRules?.includes(gameId);
  }

  /**
   * Проверяет, были ли уже показаны правила для конкретной игры
   * @param {number} gameId
   * @returns {boolean}
   */
  hasViewedGreetings(gameId) {
    return !!this.user?.gameIdsByGreetings?.includes(gameId);
  }

  /**
   * Проверяет, показывали ли уже PWA-инструкцию для игры
   * @param {number} gameId
   * @returns {boolean}
   */
  hasViewedPwaInstruction(gameId) {
    return !!this.user?.gameIdsByPwaInstructionPopup?.includes(gameId);
  }

  /**
   * Проверяет, показывали ли уже попап "использовать предыдущие ответы" для игры
   * @param {number} gameId
   * @returns {boolean}
   */
  hasUsedPrevAnswersPopup(gameId) {
    return !!this.user?.gameIdsByUsingPrevAnswersPopup?.includes(gameId);
  }

  /**
   * Проверяет, показывали ли уже страницу/модалку знакомства (meet) для игры
   * @param {number} gameId
   * @returns {boolean}
   */
  hasViewedMeet(gameId) {
    return !!this.user?.gameIdsByMeets?.includes(gameId);
  }

  /**
   * Проверяет, была ли уже завершена демонстрация для игры
   * @param {number} gameId
   * @returns {boolean}
   */
  hasFinishedDemonstration(gameId) {
    return !!this.user?.demonstrationFinishedGameIds?.includes(gameId);
  }

  async addGameIdsByPwaInstructionPopup(gameId) {
    await INTOUCH_API.requestManager(
      "add_game_ids_pwa_instruction_popup",
      { gameId, id: this.user.id },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = {
            ...this.user,
            gameIdsByPwaInstructionPopup: res.data.gameIdsByPwaInstructionPopup,
          };
        }
      })
    );
  }

  async addGameIdsByUsingPrevAnswersPopup(gameId) {
    await INTOUCH_API.requestManager(
      "add_game_ids_using_prev_answers_popup",
      { gameId, id: this.user.id },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = {
            ...this.user,
            gameIdsByUsingPrevAnswersPopup:
              res.data.gameIdsByUsingPrevAnswersPopup,
          };
        }
      })
    );
  }

  async addGameIdsByMeets(gameId, id) {
    const userId = this.user?.id || id;
    if (!userId) return;
    await INTOUCH_API.requestManager(
      "add_game_ids_by_meets",
      { gameId, id: userId },
      action((res) => {
        if (res.data.status === "ok") {
          this.user = {
            ...this.user,
            gameIdsByMeets: res.data.gameIdsByMeets,
          };
        }
      }),
      action(() => {})
    );
  }

  /**
   * Обрабатывает и сохраняет данные личного кабинета
   * Выполняет сортировку игр, устанавливает пользователя и текущую игру
   * @param {Object} data - Данные из API (user, games, invitedGames и т.д.)
   */
  setPersonalAccountData(data) {
    const {
      id,
      firstName,
      email,
      gender,
      age,
      birthday,
      games,
      isRulesAccepted,
      invitedGames,
      demonstrationFinished,
      demonstrationFinishedGameIds,
      gameIdsByGreetings,
      gameIdsByRules,
      gameIdsByPwaInstructionPopup,
      gameIdsByUsingPrevAnswersPopup,
      gameIdsByMeets,
      pointsBalance,
      refCode,
      entitlements,
    } = data;

    let currentGame;
    const currentGameStore = localStorage.getItem(CURRENT_GAME_KEY);
    if (currentGameStore && currentGameStore !== "undefined") {
      currentGame = JSON.parse(currentGameStore);
    }

    // сортировки для отображения по определенному порядку в личном кабинете
    const allGames = [...invitedGames, ...games].sort((a, b) => {
      if (
        (a.invitedUserId !== null && b.invitedUserId !== null) ||
        (a.invitedUserId === null && b.invitedUserId === null)
      ) {
        return 0;
      }
      if (
        a.invitedUserId !== null &&
        b.invitedUserId === null &&
        a?.partner?.firstName?.localeCompare(b?.partner?.firstName) &&
        new Date(a.createdAt) - new Date(b.createdAt)
      ) {
        return -1;
      }
      return 1;
    });

    let sortedGame = allGames.sort((a, b) => {
      if (id === a.userId && id === b.userId) {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }

      if (id === a.userId && id !== b.userId) {
        return new Date(a.createdAt) - new Date(b.dateInvited);
      }

      if (id !== a.userId && id === b.userId) {
        return new Date(a.dateInvited) - new Date(b.createdAt);
      }

      if (id !== a.userId && id !== b.userId) {
        return new Date(a.dateInvited) - new Date(b.dateInvited);
      }
      return 1;
    });

    if (currentGame && currentGame.invitedUserId) {
      sortedGame = sortedGame.sort((a, b) => {
        if (
          currentGame.id === a.id ||
          (currentGame === currentGame.id) === b.id
        ) {
          return -1;
        }

        return 1;
      });
    }
    // ---

    this.games = this.stopLoading(sortedGame);

    this.user = {
      id,
      firstName,
      email,
      gender,
      age,
      birthday,
      isRulesAccepted,
      demonstrationFinished,
      demonstrationFinishedGameIds,
      gameIdsByGreetings,
      gameIdsByRules,
      gameIdsByPwaInstructionPopup,
      gameIdsByUsingPrevAnswersPopup,
      gameIdsByMeets,
      // Этап 2: реф-код (шеринг-ссылка ?ref=), баллы и entitlements для видимости опций ЛК —
      // без них ломается реферальная привязка и логика «Константин/Петя»
      pointsBalance,
      refCode,
      entitlements,
    };

    // синхронизируем баланс баллов из профиля (бейдж/ЛК), не дожидаясь отдельного fetch
    if (pointsBalance !== undefined && pointsBalance !== null) {
      Points.setBalance(pointsBalance);
    }

    // сохранение this.user теперь делает reaction

    // если удалены все игры то текущую в localStorage удаляем тоже
    if (!this.games.length) {
      localStorage.removeItem(CURRENT_GAME_KEY);
    }

    // если доступна одна игра она будет выбрана по дефолту
    if (this.games.length === 1) {
      localStorage.setItem(CURRENT_GAME_KEY, JSON.stringify(this.games[0]));
      Game.setGame(this.games[0]);
    }

    // для актуализации текущей игры в localStorage
    if (currentGame) {
      const updatedGame = this.games.find((gm) => gm.id === currentGame.id);
      if (updatedGame) {
        Game.setGame(updatedGame);
        localStorage.setItem(CURRENT_GAME_KEY, JSON.stringify(updatedGame));
      }
    }

    // 10.10: при нескольких играх активная (выделенная заливкой в ЛК, к ней
    // относятся «Игра» и «Анкета» в бургер-меню) должна быть всегда. Если
    // сохранённой нет или она удалена — выбираем первую игру списка.
    const hasValidCurrent =
      currentGame && this.games.some((gm) => gm.id === currentGame.id);
    if (this.games.length > 1 && !hasValidCurrent) {
      localStorage.setItem(CURRENT_GAME_KEY, JSON.stringify(this.games[0]));
      Game.setGame(this.games[0]);
    }
  }

  async getPersonalAccountData() {
    await INTOUCH_API.requestManager(
      "current_user",
      null,
      action(async (response) => {
        this.setPersonalAccountData(response.data);
        return response.data;
      }),
      action(() => {})
    );
  }

  stopLoading(games) {
    return games.map((game) => {
      game.isLoading = false;
      return game;
    });
  }

  startLoading(games, currentGame) {
    return games.map((game) => {
      currentGame.id === game.id && (game.isLoading = true);
      return game;
    });
  }

  setUser(user) {
    this.user = { ...this.user, ...user };
  }

  manageLoading(currentGame, type) {
    const typesLoading = {
      start: () => {
        if (this.games.length) {
          this.games = this.startLoading(this.games, currentGame);
        }
      },
      stop: () => {
        if (this.games.length) {
          this.games = this.stopLoading(this.games);
        }
      },
    };
    typesLoading[type]();
  }

  addTimer(timerId) {
    this.timerLinksMap.push(timerId);
  }

  clearPersonalData() {
    this.user = {};
    this.games = [];
  }

  setStatusGame(status) {
    this.statusGame = status;
  }

  setInitialState() {
    this.games = [];
    this.user = {};
    this.statusGame = {};
    this.timerLinksMap = [];
    // сбрасываем баллы при логауте, чтобы бейдж/ЛК не показывали баланс прошлого юзера
    Points.setInitialState();
  }

  removeGameById(gameId) {
    if (!gameId) return;
    this.games = this.games.filter((gm) => gm.id !== gameId);
  }
}

export default new PersonalAccount();
